import { checkPassword, isPasswordValid } from "../utils/passwordPolicy";
import {
  Building2,
  Calendar,
  Camera,
  CheckCircle,
  Circle,
  Clock,
  Lock,
  Mail,
  Phone,
  Save,
  Shield,
  UserCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Input,
  PageLayout,
  UserProfileSkeleton,
} from "../components";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService, { API_ENDPOINTS } from "../shared/apiService";
import { formatPhone, validatePhone } from "../shared/utils/validators";

const RequirementItem = ({
  fulfilled,
  text,
}: {
  fulfilled: boolean;
  text: string;
}) => {
  return (
    <div
      className={`flex items-center gap-2 text-xs font-medium transition-colors ${fulfilled ? "text-green-600" : "text-gray-400"}`}
    >
      {fulfilled ? (
        <CheckCircle className="w-4 h-4" />
      ) : (
        <Circle className="w-4 h-4" />
      )}
      <span>{text}</span>
    </div>
  );
};

const UserProfile: React.FC = () => {
  const { t, language } = useTranslation();
  const { user, updateUser, isLoading: isAuthLoading } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [_hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    company: user?.company || "",
    phone: user?.phone || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Detectar mudanças não salvas
  const originalData = useMemo(() => {
    if (!user) return null;
    return {
      name: user.name || "",
      company: user.company || "",
      phone: user.phone || "",
    };
  }, [user]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        company: user.company || "",
        phone: user.phone || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setHasUnsavedChanges(false);
    }
  }, [user]);

  // Detectar mudanças
  useEffect(() => {
    if (!originalData) return;

    const hasChanges =
      formData.name !== originalData.name ||
      formData.company !== originalData.company ||
      formData.phone !== originalData.phone ||
      formData.newPassword !== "" ||
      formData.currentPassword !== "" ||
      formData.confirmPassword !== "";

    setHasUnsavedChanges(hasChanges);
  }, [formData, originalData]);

  const getInitials = (name: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Validação em tempo real
  const passwordMatch =
    !formData.confirmPassword ||
    formData.newPassword === formData.confirmPassword;
  const passwordRequirementsValid =
    !formData.newPassword ||
    (formData.newPassword.length >= 8 &&
      formData.newPassword.length <= 64 &&
      isPasswordValid(formData.newPassword));
  const canSave =
    (formData.name.trim() !== originalData?.name ||
      formData.company.trim() !== originalData?.company ||
      formData.phone.trim() !== originalData?.phone ||
      formData.currentPassword !== "" ||
      formData.newPassword !== "") &&
    passwordMatch &&
    passwordRequirementsValid;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Validar campos básicos
      if (!formData.name || formData.name.trim().length < 2) {
        showToastError(t("O nome deve ter pelo menos 2 caracteres"));
        setIsSaving(false);
        return;
      }

      if (formData.phone && formData.phone.trim() !== "") {
        const phoneValidation = validatePhone(formData.phone);
        if (!phoneValidation.isValid) {
          showToastError(
            t(phoneValidation.reason || "Número de telefone inválido"),
          );
          setIsSaving(false);
          return;
        }
      }

      // Validar senha se estiver alterando
      if (formData.newPassword) {
        if (!passwordRequirementsValid) {
          showToastError(t("A senha não cumpre todos os requisitos"));
          setIsSaving(false);
          return;
        }
        if (formData.newPassword !== formData.confirmPassword) {
          showToastError(t("As senhas não coincidem"));
          setIsSaving(false);
          return;
        }
        if (!formData.currentPassword) {
          showToastError(t("Digite a senha atual para alterar"));
          setIsSaving(false);
          return;
        }
      }

      const updateData: any = {
        name: formData.name,
        company: formData.company,
        phone: formData.phone,
      };

      if (formData.newPassword) {
        updateData.current_password = formData.currentPassword;
        updateData.new_password = formData.newPassword;
      }

      const data = await apiService.put(API_ENDPOINTS.USER.PROFILE, updateData);

      if (data.success) {
        // Atualizar contexto de autenticação com os novos dados
        if (data.user) {
          updateUser(data.user);
          // Atualizar formData local também
          setFormData((prev) => ({
            ...prev,
            name: data.user.name || prev.name,
            company: data.user.company || prev.company,
            phone: data.user.phone || prev.phone,
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }));
        }
        showToastSuccess(t("Perfil atualizado com sucesso!"));
      } else {
        showToastError(data.message || t("Erro ao atualizar perfil"));
      }
    } catch (error: any) {
      console.error("Erro ao atualizar perfil:", error);
      let errorMessage =
        error.message || t("Erro ao atualizar perfil. Tente novamente.");
      if (errorMessage === "Invalid current password") {
        errorMessage = t("Senha atual incorreta");
      }
      showToastError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageLayout
      title={t("Seu Perfil")}
      subtitle={t(
        "Gerencie seus dados pessoais, altere sua senha e configure suas preferências de acesso.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-4 py-4"
      loading={isAuthLoading}
      skeleton={<UserProfileSkeleton />}
    >
      {user && (
        // Sem padding próprio — o padding da página já vem do px-4 py-4 do
        // PageLayout (className acima); um wrapper com pt/pb/px adicional
        // aqui só duplicava o respiro. gap segue o padrão das demais páginas
        // deste grupo (px-4 py-4 → space-y-3, ver OrganizationUsers.tsx).
        <div className="relative z-10 space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Left Column: Main Profile Card */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="p-0 bg-white/70 backdrop-blur-xl border-purple-200 shadow-xl shadow-purple-500/5 rounded-3xl overflow-hidden sm:border">
                <div className="p-4 sm:p-5">
                  {/* Header horizontal (era empilhado/centralizado, ocupando
                      quase 300px de altura) — avatar menor ao lado do nome. */}
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-purple-100">
                    <div className="relative group shrink-0">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-linear-to-br from-purple-500 via-purple-600 to-indigo-700 p-1 shadow-lg flex items-center justify-center transform group-hover:scale-105 transition-all duration-300">
                        <div className="w-full h-full rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center text-white text-xl sm:text-2xl font-black">
                          {getInitials(formData.name || user.email)}
                        </div>
                      </div>
                      <div className="absolute -bottom-1.5 -right-1.5 p-1.5 bg-green-500 rounded-full border-2 border-white shadow-md flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-white" />
                      </div>
                      <button className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera className="h-5 w-5 text-white" />
                      </button>
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight uppercase truncate">
                        {formData.name || t("Usuário")}
                      </h2>
                      <span className="text-gray-500 font-bold text-xs sm:text-sm truncate block">
                        {user.email}
                      </span>
                    </div>
                  </div>

                  {/* Wrapper duplo removido (dois space-y-5 aninhados eram
                      redundantes — o de fora tinha um único filho). */}
                  <div className="space-y-4 animate-in fade-in duration-500">
                    {/* Profile Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Input
                          label={t("Nome Completo")}
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          placeholder={t("Seu nome completo")}
                          icon={<UserCircle className="h-5 w-5" />}
                          required
                        />
                      </div>
                      <div>
                        <Input
                          label={t("Email Institucional")}
                          value={formData.email}
                          disabled
                          icon={<Mail className="h-5 w-5" />}
                          hint={t(
                            "O email não pode ser alterado por segurança",
                          )}
                          className="opacity-60"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Input
                          label={`${t("Empresa / Organização")} (Opcional)`}
                          value={formData.company}
                          onChange={(e) =>
                            handleInputChange("company", e.target.value)
                          }
                          placeholder={t("Nome da sua empresa")}
                          icon={<Building2 className="h-5 w-5" />}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Input
                          label={`${t("Telefone / WhatsApp")} (Opcional)`}
                          value={formData.phone}
                          onChange={(e) =>
                            handleInputChange(
                              "phone",
                              formatPhone(e.target.value),
                            )
                          }
                          placeholder="(00) 00000-0000"
                          icon={<Phone className="h-5 w-5" />}
                          maxLength={15}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 border-t border-purple-100" />
                          <span className="text-gray-900 font-black text-xs uppercase tracking-widest">
                            {t("Alteração de Senha")}
                          </span>
                          <div className="flex-1 border-t border-purple-100" />
                        </div>
                      </div>

                      {/* Hidden email input for browser password managers */}
                      <input
                        type="email"
                        autoComplete="username"
                        value={user.email}
                        readOnly
                        className="hidden"
                        aria-hidden="true"
                      />

                      <div className="md:col-span-2">
                        <Input
                          label={t("Senha Atual")}
                          type="password"
                          value={formData.currentPassword}
                          onChange={(e) =>
                            handleInputChange("currentPassword", e.target.value)
                          }
                          placeholder={t("Sua senha atual")}
                          icon={<Lock className="h-5 w-5" />}
                          showPasswordToggle
                          autoComplete="current-password"
                        />
                      </div>
                      <div>
                        <Input
                          label={t("Nova Senha")}
                          type="password"
                          value={formData.newPassword}
                          onChange={(e) =>
                            handleInputChange("newPassword", e.target.value)
                          }
                          placeholder={t("Entre 8 e 64 caracteres")}
                          icon={<Lock className="h-5 w-5" />}
                          showPasswordToggle
                          error={
                            formData.newPassword && !passwordRequirementsValid
                              ? t("A senha não cumpre todos os requisitos")
                              : undefined
                          }
                          maxLength={64}
                          autoComplete="new-password"
                        />

                        {/* Password Strength Checklist */}
                        {formData.newPassword && (
                          <div className="mt-2 p-3 rounded-xl bg-purple-50/50 border border-purple-100/50 space-y-2">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                              {t("Requisitos de Senha Forte")}
                            </p>
                            <div className="flex flex-col gap-2">
                              <RequirementItem
                                fulfilled={
                                  formData.newPassword.length >= 8 &&
                                  formData.newPassword.length <= 64
                                }
                                text={t("Entre 8 e 64 caracteres")}
                              />
                              <RequirementItem
                                fulfilled={
                                  checkPassword(formData.newPassword).upper
                                }
                                text={t("Pelo menos uma letra maiúscula")}
                              />
                              <RequirementItem
                                fulfilled={
                                  checkPassword(formData.newPassword).lower
                                }
                                text={t("Pelo menos uma letra minúscula")}
                              />
                              <RequirementItem
                                fulfilled={
                                  checkPassword(formData.newPassword).number
                                }
                                text={t("Pelo menos um número")}
                              />
                              <RequirementItem
                                fulfilled={
                                  checkPassword(formData.newPassword).special
                                }
                                text={t("Um caractere especial (ex: !@#$%^&*)")}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <Input
                          label={t("Confirmar Nova Senha")}
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) =>
                            handleInputChange("confirmPassword", e.target.value)
                          }
                          placeholder={t("Repita a nova senha")}
                          icon={<Lock className="h-5 w-5" />}
                          showPasswordToggle
                          error={
                            !passwordMatch
                              ? t("As senhas não coincidem")
                              : undefined
                          }
                          autoComplete="new-password"
                        />
                      </div>
                    </div>

                    {/* Control Buttons */}
                    <div className="flex flex-row justify-end items-center gap-4 pt-4">
                      <Button
                        onClick={handleSave}
                        variant="primary"
                        size="md"
                        icon={<Save className="h-4 w-4" />}
                        isLoading={isSaving}
                        disabled={isSaving || !canSave}
                        className="flex-1 sm:flex-initial sm:min-w-[180px] rounded-xl bg-purple-600 hover:bg-purple-700 font-black text-xs uppercase tracking-widest shadow-lg shadow-purple-600/20"
                      >
                        {t("Salvar Alterações")}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column: Info Cards */}
            <div className="space-y-4">
              <Card className="p-5 bg-white/70 backdrop-blur-xl border-purple-200 shadow-xl shadow-purple-500/5 rounded-3xl sm:border">
                <h3 className="text-sm font-black text-gray-900 tracking-tight uppercase mb-3">
                  {t("Membro Desde & Último Acesso")}
                </h3>
                {/* Grid 2 colunas em vez de lista vertical — reduz a altura
                    pela metade sem perder legibilidade (labels são curtos). */}
                <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-purple-50">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-2 bg-purple-50 rounded-lg text-purple-600 shrink-0">
                      <Calendar className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        {t("Membro Desde")}
                      </p>
                      <p className="text-xs font-bold text-gray-700 truncate">
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString(
                              language === "en" ? "en-US" : "pt-BR",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              },
                            )
                          : "---"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600 shrink-0">
                      <Clock className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        {t("Último Acesso")}
                      </p>
                      <p className="text-xs font-bold text-gray-700 truncate">
                        {user.updated_at
                          ? new Date(user.updated_at).toLocaleDateString(
                              language === "en" ? "en-US" : "pt-BR",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )
                          : t("Recente")}
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="text-sm font-black text-gray-900 tracking-tight uppercase mb-3">
                  {t("Informações Adicionais")}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2.5 min-w-0 col-span-2">
                    <div className="p-2 bg-purple-50 rounded-lg text-purple-600 shrink-0">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        {t("Empresa")}
                      </p>
                      <p className="text-xs font-bold text-gray-700 truncate">
                        {formData.company || t("Não informada")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-2 bg-green-50 rounded-lg text-green-600 shrink-0">
                      <Phone className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        {t("Telefone")}
                      </p>
                      <p className="text-xs font-bold text-gray-700 truncate">
                        {user.phone || t("Não informado")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600 shrink-0">
                      <Mail className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        {t("Verificação")}
                      </p>
                      <p className="text-xs font-bold text-gray-700 truncate">
                        {t("Email Standard")}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Proteção & Ajuda combinados num único card — eram dois
                  cards separados (um roxo, um indigo escuro) só pra
                  conteúdo estático de rodapé; juntar reduz uma borda/sombra
                  inteira de altura sem perder a hierarquia visual (divisor
                  interno separa as duas seções). */}
              <div className="rounded-3xl bg-linear-to-br from-purple-600 to-indigo-900 text-white shadow-xl shadow-purple-500/20 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16" />
                <div className="relative z-10 p-5 space-y-4">
                  <div>
                    <h3 className="text-sm font-black mb-2 tracking-tight uppercase flex items-center gap-2">
                      <Shield className="w-4 h-4 shrink-0" />
                      {t("Proteção & Privacidade")}
                    </h3>
                    <p className="text-purple-100 text-xs font-medium leading-relaxed mb-3">
                      {t(
                        "Sua conta está protegida por criptografia de ponta a ponta. Mantenha suas credenciais seguras e não as compartilhe.",
                      )}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <div className="px-3 py-1 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        {t("SSL Ativo")}
                      </div>
                      <div className="px-3 py-1 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        {t("Sessão Segura")}
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <h3 className="text-sm font-black mb-1.5 uppercase tracking-tight">
                      {t("Precisa de Ajuda?")}
                    </h3>
                    <p className="text-purple-100 text-xs font-medium leading-relaxed mb-3">
                      {t(
                        "Nossa equipe está disponível para tirar qualquer dúvida sobre as configurações de sua conta.",
                      )}
                    </p>
                    <Link to="/support">
                      <Button
                        variant="secondary"
                        size="sm"
                        fullWidth
                        className="bg-white! text-gray-900! hover:bg-purple-50! rounded-lg font-black text-[10px] uppercase tracking-widest"
                      >
                        {t("Contatar Suporte")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default UserProfile;
